// BakedStylePicker.tsx — widget del step 'baked-style' (AI-styled posters).
// Qué hace: en el rail queda un disparador compacto (miniaturas de lo elegido +
//   "cambiar") y la elección real ocurre en BakedStyleModal, a pantalla grande.
//   Elegir estilo es una decisión visual: en 392px entraban 3 thumbnails por
//   fila y no se veía qué se estaba eligiendo.
// Qué exporta: BakedStylePicker.
// Quién lo usa: NovahGenerateChat.

import { useEffect, useRef, useState } from 'react';
import { BakedStyleOption } from '../types';
import { WidgetWrap, ConfirmBtn } from '../styled';
import { TriggerCard, TriggerStrip, TriggerText } from '../styleModalStyled';
import { BakedStyleModal } from './BakedStyleModal';

interface Props {
  bakedStyles: BakedStyleOption[];
  selectedStyleIds: string[];
  toggleBakedStyle: (id: string) => void;
  useLogo: boolean;
  setUseLogo: (v: boolean) => void;
  myPhotos: { id: string; url: string; name?: string }[];
  selectedPhotoIds: string[];
  togglePhoto: (id: string) => void;
  confirmBakedStyles: () => void;
  // Auto-fill del hueco verde: solo relevante si algún estilo elegido es greenbox.
  autoFillGreenbox: boolean;
  setAutoFillGreenbox: (v: boolean) => void;
  anyGreenboxSelected: boolean;
  // "Dejar la foto intacta": no la transforma con IA, solo le compone el texto/logo
  // del template encima (rutea al camino 'asset' en vez de baked-ai). Ticket 72968892.
  keepPhotoIntact: boolean;
  setKeepPhotoIntact: (v: boolean) => void;
}

export function BakedStylePicker(props: Props) {
  const {
    bakedStyles, selectedStyleIds, confirmBakedStyles,
    selectedPhotoIds, keepPhotoIntact,
  } = props;

  const [modalOpen, setModalOpen] = useState(false);

  // Se abre solo la primera vez que aparece el paso: es LA decisión del momento
  // y el rail no tiene lugar para tomarla. Si el usuario lo cierra, no vuelve a
  // abrirse solo — reabre con el disparador.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (bakedStyles.length === 0) return; // todavía cargando la lista
    autoOpenedRef.current = true;
    setModalOpen(true);
  }, [bakedStyles.length]);

  const picked = bakedStyles.filter((s) => selectedStyleIds.includes(s.id));
  const intactReady = keepPhotoIntact && selectedPhotoIds.length > 0;
  const canContinue = selectedStyleIds.length > 0 || intactReady;

  return (
    <WidgetWrap>
      <TriggerCard type="button" onClick={() => setModalOpen(true)}>
        {picked.length > 0 && (
          <TriggerStrip>
            {picked.slice(0, 3).map((s) => (
              s.preview
                ? <img key={s.id} src={s.preview} alt="" loading="lazy" />
                : null
            ))}
          </TriggerStrip>
        )}
        <TriggerText>
          {keepPhotoIntact
            ? `Using my photo as-is`
            : picked.length > 0
              ? `${picked.length} style${picked.length !== 1 ? 's' : ''} picked`
              : 'Browse poster styles'}
          <em>
            {picked.length > 0 || keepPhotoIntact
              ? 'Tap to change your picks or options'
              : `${bakedStyles.length} styles available — tap to open`}
          </em>
        </TriggerText>
      </TriggerCard>

      <ConfirmBtn onClick={confirmBakedStyles} disabled={!canContinue} style={{ marginTop: 10 }}>
        {keepPhotoIntact
          ? (selectedPhotoIds.length > 0
              ? `Continue with ${selectedPhotoIds.length} photo${selectedPhotoIds.length !== 1 ? 's' : ''}`
              : 'Pick at least one photo')
          : `Continue with ${selectedStyleIds.length} style${selectedStyleIds.length !== 1 ? 's' : ''}`}
      </ConfirmBtn>

      <BakedStyleModal {...props} open={modalOpen} onClose={() => setModalOpen(false)} />
    </WidgetWrap>
  );
}
